/**
 * Assignments & Tests → By Student.
 *
 * The same corpus from the other end: not "who sat this paper" but "what was
 * this student given, and what did they do with it".
 *
 * The row worth the whole tab is the one that says **not started** — a paper
 * somebody was given and never opened. It is invisible from the paper's own
 * page, where a student with no sitting simply is not listed.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';
import { adminApi, type StudentPaperRow, type StudentPaperState } from '../../api/adminApi';
import { userApi } from '../../api/userApi';
import './AssignmentsAndTests.css';

const STATE_BADGE: Record<StudentPaperState, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  'in-progress': 'warning',
  'not-started': 'default',
  declined: 'error',
};

const STATE_LABEL: Record<StudentPaperState, string> = {
  completed: 'completed',
  'in-progress': 'in progress',
  'not-started': 'not started',
  declined: 'declined',
};

const when = (iso?: string) => (iso ? format(new Date(iso), 'MMM d, yyyy') : '—');

export function ByStudent() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [studentId, setStudentId] = useState('');
  const [modality, setModality] = useState('');
  const [state, setState] = useState('');

  // The picker searches only once there is something to search for. An
  // unfiltered list of every user is not a chooser, it is a scroll.
  const { data: users, isFetching: searching } = useQuery({
    queryKey: ['admin-student-search', query],
    queryFn: () => userApi.getUsers({ search: query, pageSize: 10, role: 'student' }),
    enabled: query.trim().length >= 2,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-student-papers', studentId],
    queryFn: () => adminApi.getStudentPapers(studentId),
    enabled: !!studentId,
  });

  // Filtered in the browser deliberately, unlike the Tests and Assignments
  // tabs: one student's papers are already the whole result, so there is no
  // second page for a client-side filter to be wrong about.
  const rows = useMemo(() => (data?.papers ?? []).filter(p =>
    (!modality || p.modality === modality) && (!state || p.state === state)), [data, modality, state]);

  const columns = useMemo<ColumnDef<StudentPaperRow>[]>(() => [
    {
      key: 'title',
      header: 'Paper',
      render: r => (
        <div>
          <div className="aat-title">{r.title}</div>
          <div className="aat-sub">{r.modality === 'practice' ? 'Assignment' : 'Test'}</div>
        </div>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: r => <Badge variant={STATE_BADGE[r.state]} size="sm">{STATE_LABEL[r.state]}</Badge>,
    },
    {
      key: 'assigned',
      header: 'Assigned',
      render: r => (r.assignedAt
        ? (
          <div>
            <div>{when(r.assignedAt)}</div>
            {r.assignedByName && <div className="aat-sub">by {r.assignedByName}</div>}
          </div>
        )
        // No grant at all: the student reached a public paper themselves. Worth
        // saying rather than dashing, since it explains an absent due date too.
        : <span className="aat-sub">not assigned</span>),
    },
    { key: 'due', header: 'Due', render: r => when(r.dueAt) },
    {
      key: 'sat',
      header: 'Sat',
      render: r => (r.sittings
        ? (
          <div>
            <div>{when(r.lastSatAt)}</div>
            {r.sittings > 1 && <div className="aat-sub">{r.sittings} attempts</div>}
          </div>
        )
        : '—'),
    },
    {
      key: 'marks',
      header: 'Marks',
      render: r => (r.marks === undefined
        ? <span className="aat-sub">—</span>
        : <span className="aat-num">{r.marks}{r.maxMarks !== undefined && ` / ${r.maxMarks}`}</span>),
    },
    {
      key: 'report',
      header: 'Report',
      render: r => (r.reportId
        ? (
          <button
            className="btn-link"
            onClick={e => { e.stopPropagation(); navigate(`/assignments-tests/report/${r.reportId}?studentId=${studentId}`); }}
          >
            <Badge variant={r.reportStatus === 'released' ? 'success' : 'default'} size="sm">{r.reportStatus}</Badge>
            <span className="trv-open"> open</span>
          </button>
        )
        : '—'),
    },
  ], [navigate, studentId]);

  return (
    <>
      <div className="aat-filters">
        <input
          className="aat-input"
          value={query}
          placeholder="Find a student by name or email…"
          onChange={e => { setQuery(e.target.value); setStudentId(''); }}
        />
        {studentId && (
          <>
            <select value={modality} onChange={e => setModality(e.target.value)} aria-label="Type">
              <option value="">Tests and assignments</option>
              <option value="test">Tests only</option>
              <option value="practice">Assignments only</option>
            </select>
            <select value={state} onChange={e => setState(e.target.value)} aria-label="State">
              <option value="">Any state</option>
              <option value="not-started">Not started</option>
              <option value="in-progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
            </select>
          </>
        )}
      </div>

      {/* The picker: results only while nobody is chosen. */}
      {!studentId && query.trim().length >= 2 && (
        <div className="aat-picker">
          {searching && <div className="aat-sub">Searching…</div>}
          {!searching && (users?.items ?? []).length === 0 && <div className="aat-sub">No student matches that.</div>}
          {(users?.items ?? []).map(u => (
            <button
              key={u.id}
              className="aat-picker-row"
              onClick={() => { setStudentId(u.id); setQuery(u.name || u.email || u.id); }}
            >
              <span className="aat-title">{u.name || '(no name)'}</span>
              <span className="aat-sub">{u.email}</span>
            </button>
          ))}
        </div>
      )}

      {!studentId && query.trim().length < 2 && (
        <div className="aat-note">Search for a student to see what they were given and what they did with it.</div>
      )}

      {error && <div className="aat-error">{error instanceof Error ? error.message : 'Could not load this student.'}</div>}

      {studentId && (
        <DataTable<StudentPaperRow>
          columns={columns}
          data={rows}
          rowKey={r => r.questionSetId}
          loading={isLoading}
          compact
          emptyMessage="This student has no tests or assignments."
          onRowClick={r => navigate(`/assignments-tests/${r.questionSetId}`)}
          rowHref={r => `/assignments-tests/${r.questionSetId}`}
          rowTestId="student-paper-row"
        />
      )}
    </>
  );
}
